/** @jsx jsx */
import { jsx } from 'theme-ui';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Box, Button, Flex, FormControl, Heading, Input, Link, Text, useToast } from '@chakra-ui/core';
import firebase from 'firebase/app';
import moment from 'moment-timezone';
import { Fragment, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import 'react-phone-input-2/lib/material.css';
import ConfirmationCodeInput from '../../../../app/components/ConfirmationCodeInput';
import { useAuthenticationStatus } from '../../../../app/hooks/useAuthenticationStatus';
import { AuthenticationStatus } from '../../../../app/models/appModel';
import { useStoreActions } from '../../../../models/hooks';
import colors from '../../../../styles/colors';
import { formatPhoneNumber } from '../../../../utils/PhoneUtils';
import { Label } from '../../../common';
import { ActiveTabEnum } from '../../userProfileInfoType';
import {
  fullWidthBtn,
  loginBox,
  loginButtonStyle,
  loginForm,
  loginFormBox,
  loginFormTopText,
  smbLoginBox,
  smbLoginFormBox,
} from './LoginForm.styles';
import { tzToCountry } from '../../../../utils/timezoneUtils';

export type LoginFormProps = {
  activeTab: ActiveTabEnum;
  isSMB?: boolean;
};

const LoginForm = ({ activeTab, isSMB }: LoginFormProps): JSX.Element => {
  const { t } = useTranslation('login');
  const toast = useToast();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('1');
  const [countryFlag, setCountryFlag] = useState<string>('ca');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [showConfirmationCodeForm, setShowConfirmationCodeForm] = useState(false);
  const [confirmationCodeError, setConfirmationCodeError] = useState(false);
  const [appVerifier, setAppVerifier] = useState<firebase.auth.RecaptchaVerifier | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pinCodeAttempt, setPinCodeAttempt] = useState(0);
  const [countryCodeError, setCountryCodeError] = useState('');
  const parse = (value: string) => `+${countryCode}${value.replace(/\D/g, '')}`;

  const signInWithPhoneNumber = useStoreActions((actions) => actions.auth.signInWithPhoneNumber);
  const confirmPhoneNumber = useStoreActions((actions) => actions.auth.confirmPhoneNumber);

  const authenticationStatus = useAuthenticationStatus();

  // istanbul ignore next
  const getCountryShortCode = (code: string) => {
    switch (code) {
      case '1':
        return 'US';
      case '503':
        return 'SV';
      case '61':
        return 'AU';
      default:
        return 'CA';
    }
  };

  const handlePhoneNumberInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatPhoneNumber(event.target.value, getCountryShortCode(countryCode));
    setPhoneNumber(formattedValue);
  };

  const handleCountryPickerChange = (value: string, data: CountryData) => {
    setCountryCode(data.dialCode);
    setCountryFlag(data.countryCode);
    setCountryCodeError('');
    setPhoneNumber('');
  };

  const handleConfirmationCodeInputChange = (codeValue: string) => setConfirmationCode(codeValue);

  // istanbul ignore next
  const isValidateCountryCode = () => {
    if (process.env.REACT_APP_SUPPORTED_COUNTRY_CODE && process.env.REACT_APP_SUPPORTED_COUNTRY_CODE.includes(countryCode)) {
      return true;
    }
    setCountryCodeError(t('wrongCountryCode'));
    return false;
  };

  const submitPhoneNumber = () => {
    // istanbul ignore next
    if (!isValidateCountryCode()) {
      return;
    }
    setIsLoading(true);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
    signInWithPhoneNumber({
      phoneNumber: parse(phoneNumber),
      appVerifier: appVerifier as firebase.auth.RecaptchaVerifier,
    })
      .then(() => {
        setShowConfirmationCodeForm(true);
      })
      .catch((error: { code: string; message: string }) => {
        toast({
          title: error.code,
          description: error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const resendCodeButton = () => {
    setConfirmationCode('');
    setConfirmationCodeError(false);
    setShowConfirmationCodeForm(false);
    setPinCodeAttempt(0);
  };

  const submitConfirmationCode = async (verificationCode: string) => {
    setIsLoading(true);
    try {
      await confirmPhoneNumber(verificationCode);
    } catch {
      if (pinCodeAttempt <= 3) {
        setConfirmationCodeError(true);
        setPinCodeAttempt(pinCodeAttempt + 1);
      } else {
        resendCodeButton();
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (AuthenticationStatus.INVALID_USER === authenticationStatus) {
      setPhoneNumber('');
      setConfirmationCode('');
      setShowConfirmationCodeForm(false);
    }
  }, [authenticationStatus]);

  useEffect(() => {
    setAppVerifier(
      new firebase.auth.RecaptchaVerifier('recaptcha-container', {
        size: 'invisible',
      }),
    );
  }, []);
  useEffect(() => {
    const tz = moment.tz.guess();
    const mapping = tzToCountry[tz];
    // istanbul ignore next
    if (mapping) {
      setCountryFlag(mapping.flag);
      setCountryCode(mapping.code);
    } else {
      setCountryFlag('ca');
      setCountryCode('1');
    }
  }, []);

  const allowedCountries = Array.from(new Set(Object.values(tzToCountry).map(({ flag }) => flag)));

  // istanbul ignore next
  const getMaxLength = (countryShortCode: string) => {
    if (countryShortCode === 'SV') return 9;
    if (countryShortCode === 'AU') return 10;
    return 14;
  };

  const renderPhoneNumberForm = () => {
    return (
      <Fragment>
        {activeTab === ActiveTabEnum.SIGN_UP && (
          <Box mb={6} css={loginFormTopText}>
            <Heading fontSize="2xl" color="gray.900">
              {t('signUpSendCodeTitle')}
            </Heading>
            <Heading fontSize="lg" color="gray.600" whiteSpace="initial">
              {t('signUpSendCodeSubTitle')}
            </Heading>
            <Label fontSize="md" color="gray.400" whiteSpace="initial" mb={4}>
              {t('sendCodeSignUpSubtitle')}
            </Label>
          </Box>
        )}
        {activeTab === ActiveTabEnum.LOGIN && (
          <Box mb={isSMB ? 2 : 6} css={loginFormTopText}>
            <Heading fontSize="2xl" color="gray.900">
              {t('sendCodeTitle.2')}
            </Heading>
            {!isSMB && (
              <Heading fontSize="lg" color="gray.600" whiteSpace="initial">
                {t('sendCodeTitle.1')}
                {t('sendCodeTitle.3')}
              </Heading>
            )}

            <Label fontSize="md" color="gray.400" whiteSpace="initial" mb={4}>
              {t('sendCodeSubtitle')}
            </Label>
          </Box>
        )}
        <Box mb={8}>
          <Box mb={2} css={isSMB ? smbLoginFormBox : loginFormBox}>
            <Fragment>
              <FormControl mb={4}>
                <Flex>
                  <Box
                    position="relative"
                    flex="0 0 28%"
                    transition="min-width 0.2s ease"
                    onClick={
                      /* istanbul ignore next */ () => {
                        const button = document.querySelector('.selected-flag') as HTMLElement;
                        if (button) button.click();
                      }
                    }
                  >
                    <PhoneInput
                      country={countryFlag}
                      onlyCountries={allowedCountries}
                      value={countryCode}
                      onChange={handleCountryPickerChange}
                      specialLabel=""
                      disableDropdown={false}
                      inputProps={{
                        name: 'countryPicker',
                        readOnly: true,
                        'data-testid': 'CountryCodeInput',
                      }}
                      containerStyle={{
                        position: 'relative',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      inputStyle={{
                        height: '40px',
                        border: `1px solid ${colors.gray['100']}`,
                        borderRadius: '8px 0 0 8px',
                        background: 'white',
                        textAlign: 'center',
                        width: '100%',
                        paddingLeft: '45px',
                        paddingRight: '25px',
                      }}
                      buttonStyle={{
                        position: 'absolute',
                        border: `1px solid ${colors.gray['100']}`,
                        borderRight: 'none',
                        borderRadius: '8px 0 0 8px',
                        background: 'white',
                        padding: '5px',
                        cursor: 'pointer',
                      }}
                      dropdownStyle={{
                        zIndex: 2000,
                        textAlign: 'left',
                        overflowY: 'auto',
                      }}
                    />
                    <Box position="absolute" right="15px" top="50%" transform="translateY(-80%)" pointerEvents="none">
                      <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L5 5L9 1" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Box>
                  </Box>

                  <Input
                    data-testid="PhoneNumberInput"
                    value={phoneNumber}
                    onChange={handlePhoneNumberInputChange}
                    type="tel"
                    flex="1"
                    placeholder={t('phoneNumberInputPlaceholder')}
                    disabled={isLoading}
                    borderRadius="0 8px 8px 0"
                    borderLeft="none"
                    height="40px"
                    maxLength={getMaxLength(getCountryShortCode(countryCode))}
                    style={{
                      borderColor: colors.gray['100'],
                    }}
                    onKeyPress={(event) => {
                      if (event.key === 'Enter' && phoneNumber.length >= 9) {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        submitPhoneNumber();
                      }
                    }}
                  />
                </Flex>
              </FormControl>

              {isSMB && (
                <Text>
                  <Trans i18nKey="login:termsOfService">
                    By signing up for AppyHere, you are agreeing to our
                    <Link
                      color="blue.500"
                      href="https://www.iubenda.com/privacy-policy/34262405/full-legal"
                      isExternal
                      rel="noopener noreferrer"
                    >
                      terms of service
                    </Link>
                    .
                  </Trans>
                </Text>
              )}
            </Fragment>

            <Box paddingLeft={isSMB ? 0 : 3}>
              <Button
                data-testid="SubmitPhoneNumberButton"
                onClick={submitPhoneNumber}
                css={isSMB ? [loginButtonStyle, fullWidthBtn] : loginButtonStyle}
                disabled={phoneNumber.length < 9 || isLoading}
                isLoading={isLoading}
              >
                {t('sendCodeButton')}
              </Button>
            </Box>
          </Box>
          <Text fontSize="sm" color="red.500">
            {countryCodeError}
          </Text>
        </Box>
        {isSMB ? (
          <Box>
            {t('privacyTerms1')}
            <br />
            {t('privacyTerms2')}
          </Box>
        ) : (
          <Box>
            {activeTab === ActiveTabEnum.LOGIN && (
              <Label whiteSpace="initial" color="gray.600">
                {t('sendCodeFooterLabel.1')}
                <strong>{t('sendCodeFooterLabel.2')}</strong>
                {t('sendCodeFooterLabel.3')}
              </Label>
            )}
            <a href="https://hire.appyhere.com/" target="_blank" rel="noopener noreferrer">
              <Button data-testid="ResendCodeButton" w="auto" size="xs" variant="link" color={colors.blue.default}>
                <i>{t('sendCodeFooterButton')}</i>
              </Button>
            </a>
          </Box>
        )}
      </Fragment>
    );
  };

  const renderConfirmationCodeForm = () => {
    return (
      <Fragment>
        <Box css={loginFormTopText} mb={5}>
          <Label size="lg">{t('loginTitle')}</Label>
          <Label size="base" color={colors.blue.default}>
            {t('loginSubtitle')}
          </Label>
        </Box>

        <Box mb={4}>
          <Box css={isSMB ? smbLoginFormBox : loginFormBox}>
            <ConfirmationCodeInput
              verificationCode={confirmationCode}
              onChange={handleConfirmationCodeInputChange}
              onComplete={
                /* istanbul ignore next */ async (value) => {
                  await submitConfirmationCode(value);
                }
              }
            />

            <Box paddingLeft={isSMB ? 0 : 3}>
              <Button
                data-testid="LoginButton"
                onClick={() => submitConfirmationCode(confirmationCode)}
                css={isSMB ? [loginButtonStyle, fullWidthBtn] : loginButtonStyle}
                disabled={confirmationCode.length < 6 || isLoading}
                isLoading={isLoading}
                loadingText={activeTab === ActiveTabEnum.LOGIN ? t('login') : t('continue')}
              >
                {activeTab === ActiveTabEnum.LOGIN ? t('login') : t('continue')}
              </Button>
            </Box>
          </Box>

          {confirmationCodeError && (
            <Box paddingTop={3} data-testid="confirmationError">
              <Label color={colors.red['500']} bold size="sm">
                {t('wrongCode')}
              </Label>
            </Box>
          )}
        </Box>

        <Box>
          <Label>
            <i>{t('loginFooterLabel')}</i>
          </Label>
          <Button data-testid="ResendCodeBtn" size="xs" variant="link" onClick={resendCodeButton} color={colors.blue.default}>
            <i>{t('loginFooterButton')}</i>
          </Button>
        </Box>
      </Fragment>
    );
  };

  return (
    <Box data-testid="LoginForm" css={isSMB ? [loginBox, smbLoginBox] : loginBox} rounded="lg">
      <Box css={loginForm} py={6}>
        {!showConfirmationCodeForm ? renderPhoneNumberForm() : renderConfirmationCodeForm()}
      </Box>
      <div id="recaptcha-container" />
    </Box>
  );
};

export default LoginForm;
